import React from 'react';
import { InfoBox, ExerciseBox, CodeBlock, InlineCode, Result, SectionTitle, Image, Separator, CorrectionBox, Note, TableContainer, Highlight, PatternContainer, SectionHeading, SubHeading, TwoColGrid, ThreeColGrid, GridItem, Divider } from '../../../../components/course/CourseComponents';
import screen1 from '../repere2.jpg';

const Chapitre5Content = () => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
        Dans ce chapitre, nous allons gérer l'affichage et les déplacements de l'oiseau.<br/>
      Pour rappel, l'oiseau n'avance pas, ce sont les tuyaux qui reculent.<br/>
      Mais il tombe avec la gravité, et le seul moyen de le faire voler, c'est de cliquer.
        </p>
      </InfoBox>

      <Separator type="text" text="Affichage de l'oiseau" color="gray-darker" size="md" />
      <SectionTitle>🐦 1/ Affichage de l'oiseau</SectionTitle>

      <p>Pour afficher l'oiseau, on crée une variable globale qui contiendra notre image d'oiseau :</p>

      <CodeBlock language="javascript">
{`let imageOiseau;`}
      </CodeBlock>

      <p>On crée aussi un objet oiseau qui contiendra toutes les variables nécessaires à son affichage :</p>

      <CodeBlock language="javascript">
{`let oiseau = {
  x: 150,
  y: 250,
  vitesseY: 0
};`}
      </CodeBlock>

      <p>• <InlineCode>x</InlineCode> et <InlineCode>y</InlineCode> représentent sa position sur le canvas. Position de départ : (150, 250)<br/>
      • <InlineCode>vitesseY</InlineCode> représente sa vitesse sur l'axe vertical. En effet, le seul déplacement de l'oiseau sera sur cet axe.</p>

      <p>Dans <InlineCode>preload</InlineCode>, on charge l'image <InlineCode>flappy1.png</InlineCode> :</p>

      <CodeBlock language="javascript">
{`imageOiseau = loadImage("flappy1.png");`}
      </CodeBlock>

      <Note type="tip" title="💡 Images d'animation">
        <p>J'ai prévu deux autres images de l'oiseau pour créer une animation, si tu souhaite l'implémenter.<br/>
        Je te montrerai comment faire dans le jeu suivant Snake.<br/>
        Mais pour le moment, on va rester sur une image statique.</p>
      </Note>

      <p>Enfin, à la fin de <InlineCode>draw</InlineCode>, on affiche notre oiseau :</p>

      <CodeBlock language="javascript">
{`image(imageOiseau, oiseau.x, oiseau.y);`}
      </CodeBlock>

      <Note type="exercice" title="🧪 À toi de jouer : affichage de l'oiseau">
        <p>Crée la variable globale qui contiendra l'image de l'oiseau, et l'objet oiseau :</p>
        <CodeBlock language="javascript">
{`let imageOiseau;

let oiseau = {
  x: 150,
  y: 250,
  vitesseY: 0
};`}
        </CodeBlock>

        <p>Dans <InlineCode>preload</InlineCode>, charge l'image <InlineCode>flappy1.png</InlineCode> :</p>
        <CodeBlock language="javascript">
{`imageOiseau = loadImage("flappy1.png");`}
        </CodeBlock>

        <p>À la fin de <InlineCode>draw</InlineCode>, affiche l'oiseau avec la fonction <InlineCode>image</InlineCode> :</p>
        <CodeBlock language="javascript">
{`image(imageOiseau, oiseau.x, oiseau.y);`}
        </CodeBlock>

        <br/>
        <Result>Tu dois voir un oiseau statique, en position (150, 250).</Result>
      </Note>

      <Separator type="text" text="Gravité" color="gray-darker" size="md" />
      <SectionTitle>⬇️ 2/ Soumettre l'oiseau à la gravité !</SectionTitle>

      <p>On voudrait que notre oiseau tombe quand on ne touche à rien.<br/>
      La gravité est une accélération. Il faut donc l'ajouter à la vitesse de l'oiseau à chaque frame.</p>

      <Note type="warning" title="⚠️ ATTENTION : Axe Y inversé">
        <p>L'accélération est positive car l'axe des Y est inversé par rapport à un repère cartésien classique :</p>
        <ul className="list-disc list-inside mb-4">
          <li>En haut, y = 0</li>
          <li>En bas, y = 600</li>
        </ul>
        <Image src={screen1} alt="Axe Y inversé" width="370px" height="260px" borderRadius="xl" shadow={true} border={false} className="object-contain" />
        <p>Donc on ajoute une accélération positive pour que l'oiseau tombe.</p>
      </Note>

      <p><strong>Exemple :</strong></p>
      <ul className="list-disc list-inside mb-4">
        <li>Au départ, la vitesse <InlineCode>vitesseY</InlineCode> de l'oiseau vaut 0 → il ne bouge pas.</li>
        <li>La frame d'après, on ajoute la gravité (par exemple 2), la vitesse vaut 2 → l'oiseau descend de 2 pixels.</li>
        <li>Ensuite : <InlineCode>vitesseY = 4</InlineCode>, <InlineCode>oiseau.y = oiseau.y + 4</InlineCode>, etc.</li>
        <li>Ensuite : <InlineCode>vitesseY = 6</InlineCode>, <InlineCode>oiseau.y = oiseau.y + 6</InlineCode>, etc.</li>
        <li>Ensuite : <InlineCode>vitesseY = 8</InlineCode>, <InlineCode>oiseau.y = oiseau.y + 8</InlineCode>, etc.</li>
        <li>En code : <InlineCode>vitesseY = vitesseY + gravite</InlineCode>, <InlineCode>oiseau.y = oiseau.y + vitesseY</InlineCode></li>
      </ul>

      <p>Comme tu le vois dans l'exemple, l'oiseau tombe de plus en plus vite !</p>

      <p><strong>Traduction en code :</strong></p>

      <p>On crée une variable globale :</p>
      <CodeBlock language="javascript">
{`let oiseauGravite = 800; // Gravité à laquelle l'oiseau tombe`}
      </CodeBlock>

      <p>Dans <InlineCode>draw</InlineCode>, avant d'afficher l'oiseau, on modifie <InlineCode>vitesseY</InlineCode> en lui ajoutant la gravité (multipliée par <InlineCode>dt</InlineCode> pour être frame-indépendant) :</p>

      <CodeBlock language="javascript">
{`oiseau.vitesseY = oiseau.vitesseY + oiseauGravite * dt;`}
      </CodeBlock>

      <p>Puis, on change la position y de l’oiseau en ajoutant la vitesse (toujours multipliée par <InlineCode>dt</InlineCode> pour être frame-indépendant) :</p>
      <CodeBlock language="javascript">
{`oiseau.y = oiseau.y + oiseau.vitesseY * dt;`}
      </CodeBlock>

      <Note type="exercice" title="🧪 À toi de jouer : ajout de la gravité">
        <p>Crée la variable globale <InlineCode>oiseauGravite</InlineCode> :</p>
        <CodeBlock language="javascript">
{`let oiseauGravite = 800; // Gravité à laquelle l'oiseau tombe`}
        </CodeBlock>

        <p>Juste avant d'afficher l'oiseau dans <InlineCode>draw</InlineCode>, ajoute la gravité à <InlineCode>vitesseY</InlineCode>, puis ajoute <InlineCode>vitesseY</InlineCode> à sa position y.<br/>
        N'oublie pas de multiplier par <InlineCode>dt</InlineCode> pour être frame-indépendant :</p>

        <CodeBlock language="javascript">
{`oiseau.vitesseY = oiseau.vitesseY + oiseauGravite * dt;
oiseau.y = oiseau.y + oiseau.vitesseY * dt;`}
        </CodeBlock>

        <br/>
        <Result>L'oiseau tombe de plus en plus vite.</Result>

        <CorrectionBox>
          <CodeBlock language="javascript">
{`// Déplacement de l'oiseau
oiseau.vitesseY = oiseau.vitesseY + oiseauGravite * dt;
oiseau.y = oiseau.y + oiseau.vitesseY * dt;
image(imageOiseau, oiseau.x, oiseau.y);`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <Separator type="text" text="Vol de l'oiseau" color="gray-darker" size="md" />
      <SectionTitle>🚀 3/ Faire voler l'oiseau quand on clique !</SectionTitle>

      <p>C'est très simple : il suffit de modifier la <InlineCode>vitesseY</InlineCode> de l'oiseau lorsque le joueur clique :</p>

      <CodeBlock language="javascript">
{`oiseau.vitesseY = -300;`}
      </CodeBlock>

      <p>En changeant brusquement <InlineCode>vitesseY</InlineCode>, l'oiseau arrête de tomber et monte.</p>

      <Note type="tip" title="">
        <p>La vitesse est négative car l'axe Y est inversé.</p>
      </Note>

      <Note type="exercice" title="🧪 À toi de jouer : faire voler l'oiseau">
        <p>Crée une variable globale qui détermine la force du saut de l'oiseau :</p>
        <CodeBlock language="javascript">
{`let oiseauImpulsion = -300; // Force d'impulsion quand on clique`}
        </CodeBlock>

        <p>Dans la fonction <InlineCode>mousePressed</InlineCode>, définis <InlineCode>vitesseY</InlineCode> :</p>
        <CodeBlock language="javascript">
{`oiseau.vitesseY = oiseauImpulsion;`}
        </CodeBlock>

        <br/>
        <Result>À chaque clic, l'oiseau s'envole vers le haut.</Result>
      </Note>

      <p>Et voilà ! L'oiseau tombe en continu grâce à la gravité et s'envole quand on clique.</p>

      <Separator type="text" text="Code complet" color="gray-darker" size="md" />
      <SectionTitle>✅ 4/ Code complet : affichage et déplacement de l'oiseau</SectionTitle>

      <CodeBlock language="javascript">
{`// Jeu 3 : Flappy Bird
let imageFond;
let imageTuyauHaut;
let imageTuyauBas;
let imageOiseau;

let tuyauEspace = 130; // Espace entre les tuyaux
let tuyauVitesse = 150; // Vitesse de déplacement des tuyaux
let tuyauIntervalle = 2; // Intervalle entre les tuyaux, en secondes
let oiseauGravite = 800; // Gravité à laquelle l'oiseau tombe
let oiseauImpulsion = -300; // Force d'impulsion quand on clique

let tuyauTemps = 0;
let tuyaux = [];

// Oiseau
let oiseau = {
  x: 150,
  y: 250,
  vitesseY: 0
};


//--------------------------------- PRELOAD ------------------------------------------
function preload() {
  imageFond = loadImage("fond.png");
  imageTuyauHaut = loadImage("tuyau-haut.png");
  imageTuyauBas = loadImage("tuyau-bas.png");
  imageOiseau = loadImage("flappy1.png");
}

//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(600, 600);
}

//--------------------------------- DRAW ------------------------------------------
function draw() {
  background(imageFond);
  let dt = deltaTime / 1000;

  // Parcours la liste de tuyaux
  for (let i = tuyaux.length - 1; i >= 0; i--) {
    let t = tuyaux[i];

    // Déplace et affiche le tuyau
    t.x = t.x - tuyauVitesse * dt;
    afficherTuyaux(t);

    // Supprime s'il est complètement à gauche
    if (t.x + imageTuyauHaut.width < 0) {
      tuyaux.splice(i, 1);
    }
  }

  // Créer un nouveau tuyau toutes les x secondes
  tuyauTemps = tuyauTemps - dt;
  if (tuyauTemps <= 0) {
    tuyauTemps = tuyauIntervalle;
    let positionX = width;
    let positionY = random(100, 400);
    tuyaux.push({ x: positionX, y: positionY, franchi: false });
  }

  // Déplacement de l'oiseau
  oiseau.vitesseY = oiseau.vitesseY + oiseauGravite * dt;
  oiseau.y = oiseau.y + oiseau.vitesseY * dt;
  image(imageOiseau, oiseau.x, oiseau.y);
}

//--------------------------------- CLIQUE ------------------------------------------
function mousePressed() {
  oiseau.vitesseY = oiseauImpulsion;
}

//--------------------------------- AFFICHER TUYAUX ------------------------------------------
function afficherTuyaux(t) {
  image(imageTuyauHaut, t.x, t.y - imageTuyauHaut.height);
  image(imageTuyauBas, t.x, t.y + tuyauEspace);
}`}
      </CodeBlock>
    </>
  );
};

export default Chapitre5Content; 